import { useTokenHandlers } from "@core/hooks";
import {
  type AxiosServiceV2 as AxiosService,
  type TokenHandlers,
} from "@servicesV2";
import { useEffect, useState } from "react";
import { type NavigateFunction, useNavigate } from "react-router-dom";

export type ServiceConstructor<TService extends AxiosService> = new (
  tokenHandlers: TokenHandlers,
  navigate: NavigateFunction,
) => TService;

export type InitializationValue<TService extends AxiosService> = {
  service: TService;
};

export type CachingConfig = {
  refetchOnMount?: boolean;
  refetchOnWindowFocus?: boolean;
  staleTime?: number;
};

export function useService<TService extends AxiosService>(
  serviceConstructor: ServiceConstructor<TService>,
) {
  const navigateTo = useNavigate();
  const tokenHandlers = useTokenHandlers();

  const [service, setService] = useState(
    new serviceConstructor(tokenHandlers, navigateTo),
  );
  const [isReady, setIsReady] = useState(false);
  const [initWithResolvers, setInitWithResolvers] = useState(
    Promise.withResolvers<InitializationValue<TService>>(),
  );

  useEffect(
    () => setInitWithResolvers(Promise.withResolvers()),
    [serviceConstructor],
  );

  useEffect(() => {
    initWithResolvers.resolve({
      service: new serviceConstructor(tokenHandlers, navigateTo),
    });
  }, [initWithResolvers, serviceConstructor, tokenHandlers, navigateTo]);

  useEffect(() => {
    setIsReady(false);

    initWithResolvers.promise.then(({ service: serviceInstance }) => {
      setService(serviceInstance);
      setIsReady(true);
    });
  }, [initWithResolvers]);

  return {
    service,
    isReady,
    initialization: initWithResolvers.promise,
  };
}
